import { useEffect, useRef, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { Html5Qrcode } from "html5-qrcode";
import { CheckCircle2, AlertTriangle, XCircle, Loader2, Camera, ScanLine, Bus, Check, Compass, X, Users, ChevronUp } from "lucide-react";
import { api, formatDate, formatApiErrorDetail } from "@/lib/api";

const WEEKDAY = (iso) => {
  try { return new Date(iso + "T00:00:00").toLocaleDateString("en-GB", { weekday: "long" }); }
  catch { return ""; }
};
const paxOf = (b) => (b.adults || 0) + (b.children || 0) || 1;

export default function PartnerScan() {
  const { token } = useParams();
  const scannerRef = useRef(null);
  const modalRef = useRef(false);
  const busyRef = useRef(false);
  const touchStartY = useRef(null);
  const [partner, setPartner] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [scanning, setScanning] = useState(false);
  const [busy, setBusy] = useState(false);
  const [scanModal, setScanModal] = useState(null);
  const [flash, setFlash] = useState(null);
  const [camError, setCamError] = useState("");
  const [toggling, setToggling] = useState({});

  const loadRoster = useCallback(async () => {
    try {
      const r = await api.get(`/partner/${token}/roster`);
      setPartner(r.data.partner);
      setBookings(r.data.bookings || []);
      setError("");
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Unable to load your tour list.");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { loadRoster(); }, [loadRoster]);

  const dates = Array.from(new Set(bookings.map((b) => b.date))).sort();
  const checkedCount = bookings.filter((b) => b.checked_in).length;
  const totalPax = bookings.reduce((s, b) => s + paxOf(b), 0);
  const checkedPax = bookings.filter((b) => b.checked_in).reduce((s, b) => s + paxOf(b), 0);
  const noShowCount = bookings.filter((b) => b.no_show).length;

  const stopScanner = useCallback(async () => {
    const s = scannerRef.current;
    scannerRef.current = null;
    if (s) {
      try { await s.stop(); } catch (_) {}
      try { await s.clear(); } catch (_) {}
    }
  }, []);

  const showFlash = (f) => {
    setFlash(f);
    setTimeout(() => setFlash((cur) => (cur === f ? null : cur)), 4000);
  };

  const audioRef = useRef(null);
  const ensureAudio = () => {
    try {
      if (!audioRef.current) { const AC = window.AudioContext || window.webkitAudioContext; if (AC) audioRef.current = new AC(); }
      if (audioRef.current?.state === "suspended") audioRef.current.resume();
    } catch (_) {}
  };
  const feedback = (success) => {
    try { navigator.vibrate?.(success ? 80 : [50, 60, 50]); } catch (_) {}
    try {
      const ctx = audioRef.current; if (!ctx) return;
      const now = ctx.currentTime;
      const tones = success ? [880, 1320] : [300];
      tones.forEach((freq, i) => {
        const o = ctx.createOscillator(); const g = ctx.createGain();
        o.type = "sine"; o.frequency.value = freq; o.connect(g); g.connect(ctx.destination);
        const start = now + i * 0.12; const dur = success ? 0.11 : 0.35;
        g.gain.setValueAtTime(0.0001, start);
        g.gain.exponentialRampToValueAtTime(0.25, start + 0.01);
        g.gain.exponentialRampToValueAtTime(0.0001, start + dur);
        o.start(start); o.stop(start + dur + 0.02);
      });
    } catch (_) {}
  };

  const showResult = (r) => {
    modalRef.current = true;
    setScanModal(r);
    feedback(r.status === "ok" || r.status === "already");
    try { scannerRef.current?.pause(true); } catch (_) {}
  };

  const dismissResult = useCallback(() => {
    modalRef.current = false;
    setScanModal(null);
    try { scannerRef.current?.resume(); } catch (_) {}
  }, []);

  const processScan = useCallback(async (rawId) => {
    const m = String(rawId).match(/checkin\/([0-9a-fA-F-]{6,})/);
    const id = m ? m[1] : String(rawId).trim();
    busyRef.current = true;
    setBusy(true);
    try {
      const r = await api.post(`/partner/${token}/scan/${id}`);
      const s = r.data.status;
      showResult({ status: s, name: r.data.name, booking: r.data.booking, message: s === "ok" ? "" : r.data.message || "" });
      if (s === "ok" || s === "already") await loadRoster();
    } catch (e) {
      showResult({ status: "not_found", message: formatApiErrorDetail(e.response?.data?.detail) || "Scan failed." });
    }
    busyRef.current = false;
    setBusy(false);
  }, [token, loadRoster]);

  const startScanner = useCallback(async () => {
    setCamError("");
    await stopScanner();
    try {
      const h5 = new Html5Qrcode("partner-qr-reader", { verbose: false });
      scannerRef.current = h5;
      await h5.start(
        { facingMode: "environment" },
        { fps: 10, qrbox: { width: 240, height: 240 } },
        async (decodedText) => { if (modalRef.current || busyRef.current) return; await processScan(decodedText); },
        () => {}
      );
    } catch (e) {
      setCamError("Unable to access the camera. Allow camera permission or tick guests off the list below.");
    }
  }, [stopScanner, processScan]);

  useEffect(() => {
    if (scanning) startScanner(); else stopScanner();
    return () => { stopScanner(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scanning]);

  const toggleCheck = async (b) => {
    const next = !b.checked_in;
    setToggling((t) => ({ ...t, [b.id]: true }));
    setBookings((list) => list.map((x) => (x.id === b.id ? { ...x, checked_in: next, no_show: next ? false : x.no_show } : x)));
    try {
      await api.post(`/partner/${token}/checkin/${b.id}`, { checked_in: next });
    } catch (e) {
      setBookings((list) => list.map((x) => (x.id === b.id ? { ...x, checked_in: !next } : x)));
      showFlash({ status: "unpaid", name: b.customer?.full_name, message: formatApiErrorDetail(e.response?.data?.detail) });
    } finally {
      setToggling((t) => { const n = { ...t }; delete n[b.id]; return n; });
    }
  };

  const markNoShow = async (b) => {
    const next = !b.no_show;
    setToggling((t) => ({ ...t, [b.id]: true }));
    setBookings((list) => list.map((x) => (x.id === b.id ? { ...x, no_show: next, checked_in: next ? false : x.checked_in } : x)));
    try {
      await api.post(`/partner/${token}/no-show/${b.id}`, { no_show: next });
    } catch (e) {
      setBookings((list) => list.map((x) => (x.id === b.id ? { ...x, no_show: !next } : x)));
      showFlash({ status: "unpaid", name: b.customer?.full_name, message: formatApiErrorDetail(e.response?.data?.detail) });
    } finally {
      setToggling((t) => { const n = { ...t }; delete n[b.id]; return n; });
    }
  };

  if (loading) {
    return <div className="min-h-screen bg-background grain flex items-center justify-center"><Loader2 className="animate-spin text-white" size={28} /></div>;
  }
  if (error) {
    return (
      <div data-testid="partner-error" className="min-h-screen bg-background grain flex items-center justify-center px-6">
        <div className="max-w-sm text-center">
          <XCircle className="text-[#C44536] mx-auto mb-4" size={36} />
          <p className="font-display font-extrabold uppercase tracking-tight text-xl mb-2">Access link not valid</p>
          <p className="text-sm text-gray-400">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="partner-scan-page" className="min-h-screen bg-background grain">
      <header className="border-b border-white/10 bg-[#050505]/70 backdrop-blur-xl sticky top-0 z-20">
        <div className="max-w-2xl mx-auto px-6 h-20 flex items-center justify-between gap-4">
          <span className="font-display font-extrabold text-lg uppercase tracking-tighter inline-flex items-center gap-2"><Compass size={18} className="text-sky-400" /> {partner?.tour_name}</span>
          <span data-testid="partner-name" className="text-xs uppercase tracking-[0.2em] text-gray-400 truncate">{partner?.name}</span>
        </div>
      </header>

      <div className="max-w-lg mx-auto px-6 py-8">
        {/* Progress + scan toggle */}
        <div className="bg-[#0C0C0E] border border-white/10 p-5 mb-5 flex items-center justify-between gap-4">
          <div>
            <p data-testid="partner-progress" className="font-display font-extrabold text-2xl">{checkedCount}<span className="text-gray-600 text-lg">/{bookings.length}</span> <span className="text-sm text-gray-500 font-normal">boarded</span></p>
            <p className="text-xs uppercase tracking-[0.15em] text-gray-500 mt-1">{checkedPax} of {totalPax} pax{noShowCount > 0 ? <span data-testid="partner-no-show-count" className="text-[#C44536]"> · {noShowCount} no-show{noShowCount !== 1 ? "s" : ""}</span> : null}</p>
          </div>
          <button
            data-testid="partner-toggle-scanner-btn"
            onClick={() => { ensureAudio(); setScanning((s) => !s); }}
            className={`uppercase tracking-[0.15em] text-xs font-bold py-3 px-5 transition-colors inline-flex items-center gap-2 ${
              scanning ? "border border-white/30 text-white hover:bg-white/10" : "bg-white text-black hover:bg-gray-200"}`}
          >
            <ScanLine size={15} /> {scanning ? "Stop" : "Scan QR"}
          </button>
        </div>

        {/* Flash */}
        {flash && (
          <div data-testid="partner-scan-result" className={`mb-5 p-4 border flex items-center gap-3 ${
            flash.status === "ok" ? "border-emerald-500/40 bg-emerald-950/30" :
            flash.status === "already" ? "border-yellow-500/40 bg-[#2a2410]" :
            flash.status === "unpaid" ? "border-amber-500/50 bg-[#2a1f10]" :
            flash.status === "wrong_tour" ? "border-amber-500/50 bg-[#2a1f10]" :
            "border-[#C44536]/40 bg-[#2a1210]"}`}>
            {flash.status === "ok" && <CheckCircle2 className="text-emerald-400 shrink-0" size={26} />}
            {flash.status === "already" && <AlertTriangle className="text-yellow-500 shrink-0" size={26} />}
            {(flash.status === "unpaid" || flash.status === "wrong_tour") && <AlertTriangle className="text-amber-400 shrink-0" size={26} />}
            {flash.status === "not_found" && <XCircle className="text-[#C44536] shrink-0" size={26} />}
            <div className="min-w-0">
              <p data-testid="partner-scan-status" className="font-display font-bold uppercase tracking-tight text-base">
                {flash.status === "ok" ? "On the bus" : flash.status === "already" ? "Already boarded" : flash.status === "unpaid" ? "Not paid yet" : flash.status === "wrong_tour" ? "Not your tour" : "Not valid"}
              </p>
              {flash.name && <p className="text-sm text-gray-200 truncate">{flash.name}</p>}
              {flash.message && <p className="text-xs text-gray-400">{flash.message}</p>}
            </div>
          </div>
        )}

        {/* Camera */}
        {scanning && (
          <div className="mb-6">
            <p className="text-xs uppercase tracking-[0.3em] text-gray-500 mb-3 text-center">Point at the guest's QR code{busy ? " · checking…" : ""}</p>
            <div id="partner-qr-reader" data-testid="partner-qr-reader" className="w-full overflow-hidden border border-white/15 bg-black rounded-sm" style={{ minHeight: 280 }} />
            {camError && (
              <div className="mt-4 p-4 border border-[#C44536]/40 bg-[#2a1210]">
                <div className="flex items-start gap-3"><Camera size={18} className="text-[#C44536] mt-0.5" /><p className="text-sm text-gray-300">{camError}</p></div>
                <button data-testid="partner-retry-camera-btn" onClick={startScanner} className="mt-3 bg-white text-black hover:bg-gray-200 uppercase tracking-[0.15em] text-xs font-bold py-2.5 px-5 transition-colors inline-flex items-center gap-2"><Camera size={14} /> Retry Camera</button>
              </div>
            )}
          </div>
        )}

        {/* Roster grouped by day */}
        <p className="text-xs uppercase tracking-[0.3em] text-gray-500 mb-3">Passenger list · tick off each QR</p>
        {bookings.length === 0 && <p className="px-4 py-6 text-sm text-gray-500 text-center border border-white/10">No paid guests yet. Once a guest pays, they'll appear here.</p>}
        {dates.map((date) => {
          const rows = bookings.filter((b) => b.date === date).sort((a, b) => (a.customer?.full_name || "").localeCompare(b.customer?.full_name || ""));
          return (
            <div key={date} className="mb-6" data-testid={`partner-day-${date}`}>
              <div className="flex items-center gap-2 mb-2">
                <Bus size={14} className="text-sky-400" />
                <h3 className="text-xs uppercase tracking-[0.2em] text-gray-300">{WEEKDAY(date)} · {formatDate(date)}</h3>
                <span className="text-xs text-gray-600">· {rows.filter((r) => r.checked_in).length}/{rows.length}</span>
              </div>
              <div className="border border-white/10 divide-y divide-white/5">
                {rows.map((b) => (
                  <div
                    key={b.id}
                    data-testid={`partner-row-${b.id}`}
                    className={`flex items-center gap-3 px-4 py-3.5 transition-colors ${b.no_show ? "bg-[#2a1210]" : b.checked_in ? "bg-emerald-950/20" : "hover:bg-white/[0.03]"}`}
                  >
                    <button
                      type="button"
                      data-testid={`partner-check-${b.id}`}
                      onClick={() => toggleCheck(b)}
                      disabled={!!toggling[b.id]}
                      className={`shrink-0 w-7 h-7 border flex items-center justify-center transition-colors ${
                        b.checked_in ? "bg-emerald-500 border-emerald-500 text-black" : "border-white/30 text-transparent hover:border-white/60"}`}
                    >
                      {toggling[b.id] ? <Loader2 size={14} className="animate-spin text-white" /> : <Check size={16} strokeWidth={3} />}
                    </button>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm truncate ${b.no_show ? "text-[#e07a6c]" : b.checked_in ? "text-emerald-300 line-through" : "text-white"}`}>{b.customer?.full_name || "—"}</p>
                      <p className="text-xs text-gray-500 truncate">{b.slot ? `${b.slot} · ` : ""}{paxOf(b)} pax</p>
                    </div>
                    {b.checked_in && <span className="shrink-0 text-[10px] uppercase tracking-[0.12em] text-emerald-400">Boarded</span>}
                    {b.no_show && <span className="shrink-0 text-[10px] uppercase tracking-[0.12em] text-[#C44536]">No-show</span>}
                    <button
                      type="button"
                      data-testid={`partner-no-show-${b.id}`}
                      onClick={() => markNoShow(b)}
                      disabled={!!toggling[b.id]}
                      title={b.no_show ? "Undo no-show" : "Mark as no-show"}
                      className={`shrink-0 w-7 h-7 border flex items-center justify-center transition-colors ${
                        b.no_show ? "border-white/30 text-gray-400 hover:border-white/60 hover:text-white" : "border-white/15 text-gray-600 hover:border-[#C44536]/60 hover:text-[#C44536]"}`}
                    >
                      <XCircle size={15} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {scanModal && (() => {
        const s = scanModal.status;
        const b = scanModal.booking;
        const ok = s === "ok";
        const accent = ok ? "#34d399" : s === "already" ? "#eab308" : (s === "unpaid" || s === "wrong_tour") ? "#f59e0b" : "#C44536";
        const title = ok ? "On the bus" : s === "already" ? "Already boarded" : s === "unpaid" ? "Not paid yet" : s === "wrong_tour" ? "Not your tour" : "Not valid";
        return (
          <div
            data-testid="partner-scan-modal"
            onClick={dismissResult}
            onTouchStart={(e) => { touchStartY.current = e.touches[0].clientY; }}
            onTouchEnd={(e) => { if (touchStartY.current != null && touchStartY.current - e.changedTouches[0].clientY > 55) dismissResult(); touchStartY.current = null; }}
            className="fixed inset-0 z-50 flex flex-col items-center justify-center px-6 bg-black/90 backdrop-blur-sm animate-in fade-in duration-150"
          >
            <div onClick={(e) => e.stopPropagation()} className="w-full max-w-sm bg-[#0C0C0E] border rounded-sm p-8 text-center" style={{ borderColor: `${accent}66` }}>
              <div className="w-20 h-20 rounded-full border-2 flex items-center justify-center mx-auto mb-6" style={{ borderColor: accent }}>
                {ok && <CheckCircle2 size={40} style={{ color: accent }} />}
                {s === "already" && <AlertTriangle size={40} style={{ color: accent }} />}
                {(s === "unpaid" || s === "wrong_tour") && <AlertTriangle size={40} style={{ color: accent }} />}
                {s === "not_found" && <XCircle size={40} style={{ color: accent }} />}
              </div>
              <p data-testid="partner-scan-status" className="font-display font-extrabold uppercase tracking-tight text-2xl" style={{ color: accent }}>{title}</p>
              {scanModal.name && <p data-testid="partner-scan-name" className="text-white text-xl mt-3 font-medium">{scanModal.name}</p>}
              {b && (
                <div className="mt-6 border-t border-white/10 pt-6 space-y-3 text-left">
                  <div className="flex items-center justify-center gap-2">
                    <Users size={22} style={{ color: accent }} />
                    <span data-testid="partner-scan-pax" className="font-display font-extrabold text-3xl text-white">{paxOf(b)}</span>
                    <span className="text-gray-400 text-sm uppercase tracking-[0.15em]">{paxOf(b) === 1 ? "person" : "people"}</span>
                  </div>
                  {(b.adults || b.children) ? <p className="text-xs text-gray-500 text-center -mt-2">{b.adults || 0} adult{(b.adults || 0) !== 1 ? "s" : ""}{b.children ? ` · ${b.children} child${b.children !== 1 ? "ren" : ""}` : ""}</p> : null}
                  <div className="flex justify-between text-sm pt-2"><span className="text-gray-500 uppercase tracking-[0.1em] text-xs">Tour</span><span className="text-white text-right">{b.tour_name}</span></div>
                  <div className="flex justify-between text-sm"><span className="text-gray-500 uppercase tracking-[0.1em] text-xs">Date</span><span className="text-white text-right">{WEEKDAY(b.date)} {formatDate(b.date)}{b.slot ? ` · ${b.slot}` : ""}</span></div>
                </div>
              )}
              {scanModal.message && <p className="text-sm text-gray-400 mt-5">{scanModal.message}</p>}
              <button data-testid="partner-scan-done" onClick={dismissResult} className="mt-8 w-full bg-white text-black hover:bg-gray-200 uppercase tracking-[0.15em] text-xs font-bold py-4 transition-colors inline-flex items-center justify-center gap-2">
                <X size={15} /> Done — scan next
              </button>
            </div>
            <p className="text-gray-500 text-xs uppercase tracking-[0.2em] mt-6 inline-flex items-center gap-2 animate-pulse"><ChevronUp size={14} /> Swipe up to keep scanning</p>
          </div>
        );
      })()}
    </div>
  );
}
